import {Navigate} from 'react-router-dom';

import {Module, ModuleItem} from '@dms/api/shared';
import {moduleKeys} from '@dms/config';
import featureFlags from '@dms/feature-flags';
import {workshopRoutes} from '@dms/routes';
import {permissions} from '@dms/shared';
import {PostponedRequests, WorkshopOverview} from '@dms/workshop/overview';
import {
  HondaMonthlyReport,
  HyundaiDbiReport,
  MercedesMraReport,
  Report5starCstar,
  WorkshopCharts,
  WorkshopReport,
} from '@dms/workshop/reporting';
import {ServiceCampaignDetail, ServiceCampaignsOverview} from '@dms/workshop/service-campaigns';
import {CreateServiceCase, ServiceCaseDetail} from '@dms/workshop/service-case';

const content: ModuleItem[] = [
  {
    id: 'workshop-serviceCases',
    routerProps: {
      path: workshopRoutes.serviceCases,
      element: <WorkshopOverview />,
    },
    layoutProps: {
      title: 'page.workshop.labels.serviceCases',
      icon: 'sidebar/workshop_service_cases',
    },
    requiredPermission: permissions.serviceCaseRead,
  },
  {
    id: 'workshop-serviceOrders',
    routerProps: {
      path: workshopRoutes.serviceOrders,
      element: <WorkshopOverview />,
    },
    layoutProps: {
      title: 'page.workshop.labels.serviceOrders',
      icon: 'sidebar/workshop_service_orders',
    },
    requiredPermission: permissions.serviceCaseRead,
  },
  {
    id: 'workshop-directSales',
    routerProps: {
      path: workshopRoutes.directSales,
      element: <WorkshopOverview />,
    },
    layoutProps: {
      title: 'page.workshop.labels.directSales',
      icon: 'sidebar/workshop_direct_sales',
    },
    requiredPermission: permissions.serviceCaseRead,
  },
  {
    id: 'workshop-labour-operation-settings',
    routerProps: {
      path: workshopRoutes.labourOperationSettings,
      element: <WorkshopOverview />,
    },
    layoutProps: {
      title: 'page.workshop.labels.labourOperationSettings',
      icon: 'sidebar/workshop_labour_operation_settings',
    },
    requiredPermission: permissions.readLabourCatalogue,
  },
  {
    id: 'workshop-draftServiceCase',
    routerProps: {
      path: workshopRoutes.draftServiceCase,
      element: <CreateServiceCase />,
    },
    requiredPermission: permissions.serviceCaseRead,
  },
  {
    id: 'workshop-createServiceCase',
    routerProps: {
      path: workshopRoutes.createServiceCase,
      element: <CreateServiceCase />,
    },
    requiredPermission: permissions.serviceCaseRead,
    layoutProps: {
      title: 'page.workshop.labels.newServiceCase',
      isPartOfCreateMenuItem: true,
    },
  },
  {
    id: 'workshop-serviceCase-detail',
    routerProps: {
      path: workshopRoutes.serviceCaseDetail,
      element: <ServiceCaseDetail />,
    },
    requiredPermission: permissions.serviceCaseRead,
  },
  {
    id: 'workshop-service-campaigns',
    routerProps: {
      path: workshopRoutes.serviceCampaigns,
      element: <ServiceCampaignsOverview />,
    },
    layoutProps: {
      title: 'page.workshop.labels.serviceCampaigns',
      icon: 'sidebar/workshop_service_campaigns',
    },
    requiredPermission: permissions.serviceCaseRead,
  },
  {
    id: 'workshop-service-campaigns-Hyundai',
    routerProps: {
      path: workshopRoutes.serviceCampaignsUnfinished,
      element: <ServiceCampaignsOverview />,
    },
    requiredPermission: permissions.serviceCaseRead,
  },
  {
    id: 'workshop-service-campaign-detail',
    routerProps: {
      path: workshopRoutes.serviceCampaignDetail,
      element: <ServiceCampaignDetail />,
    },
    requiredPermission: permissions.serviceCaseRead,
  },
  {
    id: 'workshop-postponed-requests',
    routerProps: {
      path: workshopRoutes.postponedRequests,
      element: <PostponedRequests />,
    },
    layoutProps: {
      title: 'page.workshop.labels.postponedRequests',
      icon: 'sidebar/workshop_postponed_requests',
    },
    requiredPermission: permissions.serviceCaseRead,
  },
  {
    id: 'workshop-reporting-group',
    routerProps: {
      path: workshopRoutes.reporting,
    },
    layoutProps: {
      title: 'entity.order.labels.reporting',
      icon: 'sidebar/analytics',
    },
    requiredPermissionAtLeastOne: [
      permissions.workshopReportingRead,
      permissions.workshopReportingMercedesMraRead,
      permissions.workshopReportingFiveStarRead,
      permissions.workshopReportingCStarRead,
      permissions.workshopReportingHyundaiDbiRead,
    ],
  },
  {
    id: 'workshop-reporting',
    parentId: 'workshop-reporting-group',
    routerProps: {
      path: workshopRoutes.reporting,
      element: <WorkshopReport />,
    },
    layoutProps: {
      title: 'analytics.label.workshopReports',
      icon: 'sidebar/analytics',
    },
    requiredPermission: permissions.workshopReportingRead,
  },
  {
    id: 'workshop-reporting-charts',
    parentId: 'workshop-reporting-group',
    routerProps: {path: workshopRoutes.reportingCharts, element: <WorkshopCharts />},
    layoutProps: {title: 'analytics.label.reportingCharts', icon: 'sidebar/analytics'},
    requiredPermission: permissions.workshopReportingRead,
    requiredFeatureFlag: featureFlags.ADVANCED_MECHANICS_LABOUR_ITEMS_REPORTING,
  },
  // Written out literally on purpose: the DMS route-catalog generator only resolves a literal
  // string `id` plus a direct `workshopRoutes.<member>` path reference, so items produced by a
  // `map`/`flatMap` never reach the catalog and the AI assistant cannot deep-link to them.
  {
    id: 'workshop-chart-sales-performance',
    routerProps: {path: workshopRoutes.reportingChartSales, element: <WorkshopCharts />},
    requiredPermission: permissions.workshopReportingRead,
    requiredFeatureFlag: featureFlags.ADVANCED_MECHANICS_LABOUR_ITEMS_REPORTING,
  },
  {
    id: 'workshop-chart-redirect-sales-performance',
    routerProps: {
      path: `${workshopRoutes.reporting}/sales-performance`,
      element: <Navigate to={workshopRoutes.reportingChartSales} replace />,
    },
    requiredPermission: permissions.workshopReportingRead,
    requiredFeatureFlag: featureFlags.ADVANCED_MECHANICS_LABOUR_ITEMS_REPORTING,
  },
  {
    id: 'workshop-chart-mechanic-labour',
    routerProps: {path: workshopRoutes.reportingChartMechanic, element: <WorkshopCharts />},
    requiredPermission: permissions.workshopReportingRead,
    requiredFeatureFlag: featureFlags.ADVANCED_MECHANICS_LABOUR_ITEMS_REPORTING,
  },
  {
    id: 'workshop-chart-redirect-mechanic-labour',
    routerProps: {
      path: `${workshopRoutes.reporting}/mechanic-labour`,
      element: <Navigate to={workshopRoutes.reportingChartMechanic} replace />,
    },
    requiredPermission: permissions.workshopReportingRead,
    requiredFeatureFlag: featureFlags.ADVANCED_MECHANICS_LABOUR_ITEMS_REPORTING,
  },
  {
    id: 'workshop-reporting-tab',
    routerProps: {
      path: workshopRoutes.reportingTab,
      element: <WorkshopReport />,
    },
    requiredPermission: permissions.workshopReportingRead,
  },
  {
    id: 'workshop-reporting-mercedes-mra',
    parentId: 'workshop-reporting-group',
    routerProps: {
      path: workshopRoutes.reportingMercedesMra,
      element: <MercedesMraReport />,
    },
    layoutProps: {
      title: 'analytics.label.mercedesMra',
      icon: 'sidebar/analytics',
    },
    requiredPermission: permissions.workshopReportingMercedesMraRead,
    dynamicAccess: 'mercedesMraReporting',
  },
  {
    id: 'workshop-reporting-mercedes-mra-custom-reports',
    routerProps: {
      path: workshopRoutes.reportingMercedesMraCustomReports,
      element: <MercedesMraReport />,
    },
    requiredPermission: permissions.workshopReportingMercedesMraRead,
  },
  {
    id: 'workshop-reporting-mercedes-mra-inactive-reports',
    routerProps: {
      path: workshopRoutes.reportingMercedesMraInactiveReports,
      element: <MercedesMraReport />,
    },
    requiredPermission: permissions.workshopReportingMercedesMraRead,
  },
  {
    id: 'workshop-reporting-5star',
    parentId: 'workshop-reporting-group',
    routerProps: {
      path: workshopRoutes.reportingFiveStar,
      element: <Report5starCstar type="5star" />,
    },
    layoutProps: {
      title: 'analytics.label.5starReports',
      icon: 'sidebar/analytics',
    },
    requiredPermission: permissions.workshopReportingFiveStarRead,
    dynamicAccess: 'merc5StarReporting',
  },
  {
    id: 'workshop-reporting-cstar',
    parentId: 'workshop-reporting-group',
    routerProps: {
      path: workshopRoutes.reportingCStar,
      element: <Report5starCstar type="cstar" />,
    },
    layoutProps: {
      title: 'analytics.label.CstarReports',
      icon: 'sidebar/analytics',
    },
    requiredPermission: permissions.workshopReportingCStarRead,
    dynamicAccess: 'mercCstarReporting',
  },
  {
    id: 'workshop-reporting-honda-monthly-report',
    parentId: 'workshop-reporting-group',
    routerProps: {
      path: workshopRoutes.reportingHondaMonthlyReport,
      element: <HondaMonthlyReport />,
    },
    layoutProps: {
      title: 'analytics.label.hondaMonthlyReports',
      icon: 'sidebar/analytics',
    },
    requiredPermission: permissions.workshopReportingRead,
    dynamicAccess: 'hondaMonthlyReportReporting',
  },
  {
    id: 'workshop-reporting-hyundai-dbi',
    parentId: 'workshop-reporting-group',
    routerProps: {
      path: workshopRoutes.reportingHyundaiDbi,
      element: <HyundaiDbiReport />,
    },
    layoutProps: {
      title: 'analytics.label.hyundaiDbiReports',
      icon: 'sidebar/analytics',
    },
    requiredPermission: permissions.workshopReportingHyundaiDbiRead,
    dynamicAccess: 'hyundaiDbiReporting',
  },
];

export const WorkshopModule: Module = {
  id: moduleKeys.workshop,
  layoutProps: {
    title: 'module.workshop.title',
    icon: 'sidebar/workshop',
  },
  routerProps: {
    path: workshopRoutes.overview,
    element: <WorkshopOverview />,
  },
  requiredPermission: permissions.workshopRead,
  requiredPermissionAtLeastOne: [
    permissions.serviceCaseRead,
    permissions.readLabourCatalogue,
    permissions.workshopInterestRead,
    permissions.workshopReportingRead,
    permissions.workshopReportingMercedesMraRead,
    permissions.workshopReportingFiveStarRead,
    permissions.workshopReportingCStarRead,
    permissions.workshopReportingHyundaiDbiRead,
  ],
  content,
};
